/**
 * @fileoverview Overview Timeline Chart
 * @description Multi-series area chart showing requests, failures, and avg duration over time.
 */

import React from 'react';
import { Box, Typography, Paper, Skeleton } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { formatTimestamp } from '../utils/timeRangeUtils';

const OverviewTimeline = ({ data, loading, timeRange }) => {
  if (loading) {
    return <Skeleton variant="rounded" height={350} sx={{ mb: 3 }} />;
  }

  const rows = data?.rows || [];
  if (!rows.length) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No overview data available for this time range.</Typography>
      </Paper>
    );
  }

  const timestamps = rows.map((r) => new Date(r.timestamp));
  const requestCounts = rows.map((r) => r.request_count ?? r.count ?? 0);
  const failedCounts = rows.map((r) => r.failed_count ?? r.failed ?? 0);
  const avgDurations = rows.map((r) => r.avg_duration != null ? Math.round(r.avg_duration) : 0);

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#01426A' }}>
        Overview Timeline
      </Typography>
      <Box sx={{ width: '100%', height: 350 }}>
        <LineChart
          height={320}
          xAxis={[
            {
              data: timestamps,
              scaleType: 'time',
              valueFormatter: (v) => formatTimestamp(v, timeRange),
            },
          ]}
          yAxis={[
            { id: 'count', label: 'Count' },
            { id: 'duration', label: 'Avg Duration (ms)', position: 'right' },
          ]}
          series={[
            {
              data: requestCounts,
              label: 'Requests',
              color: '#01426A',
              area: true,
              yAxisId: 'count',
              showMark: false,
            },
            {
              data: failedCounts,
              label: 'Failures',
              color: '#d32f2f',
              area: true,
              yAxisId: 'count',
              showMark: false,
            },
            {
              data: avgDurations,
              label: 'Avg Duration (ms)',
              color: '#0097A9',
              yAxisId: 'duration',
              showMark: false,
            },
          ]}
          slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' } } }}
        />
      </Box>
    </Paper>
  );
};

export default OverviewTimeline;
